import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { mockData } from '../../data/mock';
import { revealVariants, staggerContainer } from '../../utils/motion';
import { useInViewOnce } from '../../hooks/useInViewOnce';

export const EnhancedTestimonialsSection = () => {
  const [ref, isInView] = useInViewOnce();
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % mockData.testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + mockData.testimonials.length) % mockData.testimonials.length);
  };

  React.useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 5000); // Auto-advance every 5 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-[#FF5C8A]/5 to-[#FF8C42]/5 rounded-full transform -translate-x-36 -translate-y-36" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-[#1F3C88]/5 to-[#FF5C8A]/5 rounded-full transform translate-x-48 translate-y-48" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.div
            variants={revealVariants}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 text-[#1F3C88] px-4 py-2 rounded-full text-sm font-medium mb-6"
          >
            <Star size={16} className="text-[#FF5C8A]" />
            <span>Client Success Stories</span>
          </motion.div>
          
          <motion.h2 
            variants={revealVariants}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            What Our <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">Clients Say</span>
          </motion.h2>
          
          <motion.p 
            variants={revealVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Real testimonials from real clients who have experienced exceptional results 
            through our consulting expertise and dedicated service.
          </motion.p>
        </motion.div>

        {/* Desktop Carousel */}
        <div className="hidden lg:block">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="relative max-w-6xl mx-auto"
          >
            {/* Navigation Buttons */}
            <motion.button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-16 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group z-10"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronLeft size={24} className="text-[#1F3C88] group-hover:text-[#FF5C8A] transition-colors" />
            </motion.button>

            <motion.button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-16 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group z-10"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronRight size={24} className="text-[#1F3C88] group-hover:text-[#FF5C8A] transition-colors" />
            </motion.button>

            {/* Testimonials Grid */}
            <div className="grid grid-cols-3 gap-8">
              {mockData.testimonials.map((testimonial, index) => {
                const offset = (index - currentIndex + mockData.testimonials.length) % mockData.testimonials.length;
                const isCenter = offset === 0;
                const isAdjacent = offset === 1 || offset === mockData.testimonials.length - 1;
                
                return (
                  <motion.div
                    key={index}
                    className={`relative ${isCenter ? 'z-20' : isAdjacent ? 'z-10' : 'z-0'}`}
                    animate={{
                      scale: isCenter ? 1.05 : isAdjacent ? 0.95 : 0.9,
                      opacity: isCenter ? 1 : isAdjacent ? 0.8 : 0.6,
                      y: isCenter ? 0 : isAdjacent ? 20 : 40
                    }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  >
                    <TestimonialCard testimonial={testimonial} isActive={isCenter} />
                  </motion.div>
                );
              })}
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center space-x-3 mt-8">
              {mockData.testimonials.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentIndex === index 
                      ? 'bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] scale-125' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                />
              ))}
            </div>
          </motion.div>
        </div>

        {/* Mobile Stack */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="lg:hidden space-y-8"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
            >
              <TestimonialCard testimonial={mockData.testimonials[currentIndex]} isActive={true} />
            </motion.div>
          </AnimatePresence>

          {/* Mobile Navigation */}
          <div className="flex justify-center items-center space-x-4">
            <button
              onClick={prevTestimonial}
              className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center"
            >
              <ChevronLeft size={20} className="text-[#1F3C88]" />
            </button>

            <div className="flex space-x-2">
              {mockData.testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentIndex === index 
                      ? 'bg-[#FF5C8A] w-6' 
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center"
            >
              <ChevronRight size={20} className="text-[#1F3C88]" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonial Card Component
const TestimonialCard = ({ testimonial, isActive }) => {
  return (
    <div className={`bg-gradient-to-br from-white to-slate-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 border border-gray-100 relative h-full ${isActive ? 'border-[#FF5C8A]/30' : ''}`}>
      {/* Quote Icon */}
      <motion.div
        className="absolute top-4 right-4 w-12 h-12 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 rounded-full flex items-center justify-center"
        animate={{ rotate: isActive ? 360 : 0 }}
        transition={{ duration: 0.8 }}
      >
        <Quote size={20} className="text-[#FF5C8A]" />
      </motion.div>

      {/* Stars */}
      <div className="flex items-center space-x-1 mb-6">
        {[...Array(testimonial.rating)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.1, duration: 0.3 }}
          >
            <Star size={16} className="text-[#FF8C42] fill-current" />
          </motion.div>
        ))}
      </div>

      {/* Testimonial Text */}
      <blockquote className="text-gray-700 leading-relaxed mb-6 italic text-lg">
        "{testimonial.text}"
      </blockquote>

      {/* Author */}
      <div className="flex items-center space-x-4">
        <motion.div
          className="w-14 h-14 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] rounded-full flex items-center justify-center flex-shrink-0"
          whileHover={{ scale: 1.1 }}
        >
          <span className="text-white font-bold text-lg">
            {testimonial.name.split(' ').map(n => n[0]).join('')}
          </span>
        </motion.div>
        <div>
          <div className="font-semibold text-gray-900 text-lg">{testimonial.name}</div>
          <div className="text-[#FF5C8A] font-medium">{testimonial.company}</div>
        </div>
      </div>

      {/* Active Glow Effect */}
      {isActive && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-[#FF5C8A]/5 to-[#FF8C42]/5 rounded-2xl"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}
    </div>
  );
};